import "./SiteBase.scss";

// Import core bootstrap components using this method to ensure they are ALWAAAAAYS there and ready for use
import { CustomFileInput } from "./CustomFileInput";
import { DropOption } from "./DropOption";
import queryString from 'query-string';
import { TrackJS } from "trackjs";

import "bootstrap/js/dist/alert";
import "bootstrap/js/dist/button";
import "bootstrap/js/dist/collapse";
import "bootstrap/js/dist/dropdown";
import "bootstrap/js/dist/offcanvas";
import "bootstrap/js/dist/tab";

import "lazysizes/plugins/print/ls.print";
import "lazysizes/plugins/respimg/ls.respimg";
import "lazysizes";

import { ChatBase } from "@Base/Engagement/Chat/ChatBase";
import { CouponManager } from "@Utilities/CouponManager";
import { DataPrivacyPolicy } from "@Utilities/DataPrivacyPolicy";
import { FooterBase } from "./FooterBase";
import { HeaderBase } from "./HeaderBase";
import { GoogleTopQualityStores } from "@ThirdParty/GoogleTopQualityStores";
import { EngagementProvider } from "@Base/Engagement/Engagement";
import { IContentCallback, ISpecialContent, ContentService } from "@Api/ContentService";
import { SiteListBase } from "./SiteListBase";
import { Tenant } from "@Utilities/Tenant";
import { Tracking } from "@Utilities/Tracking";
import { TransitBannerBase } from "./TransitBannerBase";
import { Window } from "./_Window";

export class SiteBase
{
	public Chat: ChatBase;
	public readonly couponManager: CouponManager;
	public SiteList: SiteListBase;
	public Header: HeaderBase;
	public Footer: FooterBase;
	public TransitBanner: TransitBannerBase;
	private readonly contentService: ContentService;
	private readonly dataPrivacyPolicy: DataPrivacyPolicy;
	private readonly engagementProvider: EngagementProvider;
	private CartLinksConfigured = false;
	private ContentResponseCallbacks: Array<IContentCallback> = [];
	private readonly tracking: Tracking;

	constructor()
	{
		new Window();
		this.contentService = new ContentService();
		this.couponManager = new CouponManager();
		this.dataPrivacyPolicy = new DataPrivacyPolicy();
		this.engagementProvider = new EngagementProvider();
		this.tracking = new Tracking();
	}

	public async Initialize(): Promise<void>
	{
		this.InitializeScriptErrorLogging();
		new GoogleTopQualityStores();
		this.tracking.VisitCountCheck();
		await this.GetContent();
		new CustomFileInput();
		new DropOption();
		this.ProcessQueryStrings();
	};

	private AddSpecialContent = (specialContent: Array<ISpecialContent>): void =>
	{
		specialContent.forEach(content =>
		{
			const element = document.querySelector(`#${content.Element}`);
			const frag = document.createRange().createContextualFragment(content.Html);
			const contentNode = frag.firstChild;

			if (element !== null)
			{
				if (content.ReplaceExisting)
				{
					element.innerHTML = content.Html;
				}
				else
				{
					element.prepend(contentNode);
				}
			}

			if (content.Element === "skin-customer-message-home" || content.Element === "skin-customer-message")
			{
				document.querySelector(`#${content.Element}`).classList.add("special-content");
			}
		});
	};

	private TriggerContentCallbacks = (): void =>
	{
		this.ContentResponseCallbacks.forEach((event) =>
		{
			event();
		});
	};

	/// <summary>Must be done before SiteBase.Initialize is called</summary>
	public RegisterContentCallback = (callback: IContentCallback): void =>
	{
		this.ContentResponseCallbacks.push(callback);
	};

	public GetContent = async (secondAttempt = false): Promise<void> =>
	{
		this.contentService.ApiCall()
			.then(() =>
			{
				if (typeof ContentService !== "undefined")
				{
					if (typeof ContentService.Response !== "undefined")
					{
						Tenant.SecureDomainUrl = ContentService.Response.SecureDomainUrl;
						Tenant.WebsiteName = ContentService.Response.WebsiteName;
					}
					else
					{
						// Fallback in case Response fails for some reason
						Tenant.SecureDomainUrl = `https://${document.location}/`;

						if (Tenant.SecureDomainUrl.includes("halloweencostumes"))
						{
							Tenant.WebsiteName = "HalloweenCostumes.com";
						}
						else if (Tenant.SecureDomainUrl.includes("halloween"))
						{
							Tenant.WebsiteName = "Halloween.com";
						}
						else
						{
							Tenant.WebsiteName = "FUN.com";
						}
					}
				}

				if (typeof this.Header !== "undefined")
				{
					this.Header.TriggerContentCallbacks();
				}

				this.couponManager.TriggerContentCallbacks();
				this.dataPrivacyPolicy.TriggerContentCallbacks();
				this.engagementProvider.TriggerContentCallbacks();

				this.ProcessExperiment();
				this.ProcessSpecialContent();
				this.ProcessCartLinks();

				if (typeof this.Footer !== "undefined")
				{
					this.Footer.TriggerContentCallbacks();
				}

				if (typeof this.TransitBanner !== "undefined")
				{
					this.TransitBanner.TriggerContentCallbacks();
				}

				this.TriggerContentCallbacks();
				window.ContentRendered = true;
				return Promise.resolve();
			})
			.catch((e: Error) =>
			{
				// Reattempt GetContent
				if (!secondAttempt)
				{
					this.GetContent(true);
				}
				else
				{
					const error = `GetContent Error: ${e}`;
					console.error(error);
					return Promise.reject(`GetContent Error: ${e}`);
				}
			});
	};

	private InitializeScriptErrorLogging = (): void =>
	{
		const trackJsSettings = window.TrackJSSettings;

		if (typeof trackJsSettings !== "undefined")
		{
			TrackJS.install(trackJsSettings);
			window.trackJs = TrackJS;
		}
	};

	/// <summary>Configures the cart links.</summary>
	private ProcessCartLinks = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			if (ContentService.Response.IsDirectToCheckout !== undefined && ContentService.Response.IsDirectToCheckout !== null && ContentService.Response.IsDirectToCheckout)
			{
				if (!this.CartLinksConfigured)
				{
					const url = Tenant.SecureDomainUrl + "cart/check-out.aspx";
					const links = document.querySelectorAll('a[href$="/cart/"]');

					links.forEach((link: HTMLLinkElement) =>
					{
						link.href = url;
					});

					this.CartLinksConfigured = true;
				}
			}
		}
	};

	private ProcessQueryStrings = (): void =>
	{
		const parsed = queryString.parse(location.search);
		const unique = parsed.unique as string;
		const ogid = parsed.ogid as string;

		if (unique)
		{
			this.engagementProvider.AddRecommendationClick(unique);
		}

		if (ogid)
		{
			this.tracking.RestoreCart(ogid);
		}
	};

	private ProcessExperiment = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			const experimentID = ContentService.Response.ExperimentID;

			if (typeof experimentID !== "undefined" && experimentID !== null && experimentID !== "")
			{
				this.tracking.AddExperimentToGa(experimentID);
			}
		}
	};

	private ProcessSpecialContent = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			const scheduledContent = ContentService.Response.ScheduledContent;
			const abContent = ContentService.Response.ABContent;

			if (scheduledContent !== undefined && scheduledContent !== null)
			{
				this.AddSpecialContent(scheduledContent);
			}

			if (abContent !== undefined && abContent !== null)
			{
				this.AddSpecialContent(abContent);
			}
		}
	};
}